import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";

const productMeta = [
  { slug: "brake-components", titleKey: "range.div1Title", shortKey: "range.div1Short", image: "/products/v2/brake-hero.jpg", fit: "fill-brake" },
  { slug: "other-parts", titleKey: "range.div2Title", shortKey: "range.div2Short", image: "/products/v2/gear-pump.png", fit: "fill-square" },
  { slug: "tyres-equipment", titleKey: "range.div3Title", shortKey: "range.div3Short", image: "/products/v2/offroad-tyres.png", fit: "cover" },
  { slug: "alignment-balancing", titleKey: "range.div4Title", shortKey: "range.div4Short", image: "/products/v2/wheel-balancer.png", fit: "cover" },
];

export default function ProductRange({ compact = false }) {
  const { t } = useLang();
  return (
    <section
      id="range"
      data-testid="range-section"
      className={`relative ${compact ? "py-16 md:py-24" : "py-20 md:py-28"} bg-[#D6E2F0]`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="text-center mb-12 md:mb-14">
          <p className="overline-maroon mb-4">{t("range.overline")}</p>
          <h2 className="font-serif-display font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0F1E33]">
            {t("range.title1")} <span className="text-[#1E3A5F] italic">{t("range.titleItalic")}</span>
          </h2>
          <div className="mx-auto mt-6 w-24 hairline-gold" />
          <p className="mt-6 max-w-2xl mx-auto text-[#1F2D40] text-lg md:text-xl leading-relaxed">
            {t("range.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {productMeta.map((p, idx) => {
            const title = t(p.titleKey);
            const short = t(p.shortKey);
            return (
              <motion.article
                key={p.slug}
                data-testid={`product-card-${p.slug}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="group bg-white border border-[#D5DEE8] hover:border-[#B8860B] transition-colors flex flex-col"
              >
                <div className={`relative w-full overflow-hidden ${
                  p.fit === "fill-brake" || p.fit === "fill-square"
                    ? "h-72 md:h-80 lg:h-72 bg-white"
                    : "h-52 md:h-56 lg:h-52 bg-[#D6E2F0]"
                }`}>
                  <img
                    src={p.image}
                    alt={title}
                    loading="lazy"
                    className={`w-full h-full ${
                      p.fit === "contain"
                        ? "object-contain p-4"
                        : p.fit === "fill-brake"
                          ? "object-cover object-top"
                          : p.fit === "fill-square"
                            ? "object-cover object-top"
                            : "object-cover"
                    } group-hover:scale-[1.03] transition-transform duration-700`}
                  />
                </div>

                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <h3 className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-[#1F2D40] leading-relaxed flex-1">
                    {short}
                  </p>

                  <div className="mt-5 pt-4 border-t border-[#D5DEE8] flex justify-end">
                    <Link
                      to="/quote"
                      data-testid={`product-quote-${p.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#6B1F2C] text-[#D4AF37] hover:bg-[#4A1620] transition-colors text-[10px] md:text-xs font-mono uppercase tracking-widest font-semibold"
                    >
                      {t("common.requestQuoteShort")}
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {compact && (
          <div className="mt-12 text-center">
            <Link
              to="/range"
              data-testid="view-all-products"
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-transparent border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-[#D6E2F0] transition-colors tracking-wide"
            >
              {t("common.viewAll")}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
